import { useEffect, useMemo, useRef, useState } from 'react';
import { Avatar, Chip } from '@/components/ui';
import { getAvatarColorClass } from '@/shared/lib/avatar';
import { getInitials } from '@/shared/lib/names';
import type { User } from '@/shared/types/user';
import { addThreadParticipant, removeThreadParticipant } from './thread-utils';

const MAX_SUGGESTIONS = 8;

export default function ThreadParticipantsInput({
  users,
  selectedIds,
  currentUserId,
  onChange,
}: {
  users: User[];
  selectedIds: string[];
  currentUserId: string;
  onChange: (ids: string[]) => void;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const suggestions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return users
      .filter((user) => !selected.has(user.id))
      .filter((user) => {
        if (!normalizedQuery) {
          return true;
        }

        return `${user.data.displayName} ${user.data.email}`
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .slice(0, MAX_SUGGESTIONS);
  }, [query, selected, users]);

  useEffect(() => {
    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    window.addEventListener('mousedown', closeOnOutsideClick);

    return () => window.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const addParticipant = (userId: string) => {
    onChange(addThreadParticipant(selectedIds, userId));
    setQuery('');
    setOpen(true);
    inputRef.current?.focus();
  };

  const removeParticipant = (userId: string) => {
    onChange(removeThreadParticipant(selectedIds, userId));
  };

  return (
    <div className='space-y-1.5'>
      <label
        htmlFor='private-thread-participants'
        className='block text-sm font-medium text-slate-700'
      >
        Participants
      </label>
      <div ref={wrapperRef} className='relative'>
        <div
          className='flex min-h-12 w-full flex-wrap items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-2 shadow-sm transition focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100'
          onClick={() => inputRef.current?.focus()}
        >
          {selectedIds.map((userId) => {
            const user = usersById.get(userId);
            const name = user?.data.displayName || 'Unknown participant';

            return (
              <Chip
                key={userId}
                isCloseable
                onClose={() => removeParticipant(userId)}
                className='max-w-full bg-slate-100 py-1 pl-1 pr-1.5 text-sm'
              >
                <span className='flex min-w-0 items-center gap-1.5'>
                  <Avatar
                    name={name}
                    size='sm'
                    showFallback
                    getInitials={() => getInitials(name)}
                    className={`h-5 w-5 shrink-0 text-[9px] ${getAvatarColorClass(name)}`}
                  />
                  <span className='max-w-44 truncate'>
                    {name}
                    {userId === currentUserId ? ' (you)' : ''}
                  </span>
                </span>
              </Chip>
            );
          })}
          <input
            ref={inputRef}
            id='private-thread-participants'
            role='combobox'
            aria-expanded={open}
            aria-controls='private-thread-participant-options'
            aria-autocomplete='list'
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && suggestions[0]) {
                event.preventDefault();
                addParticipant(suggestions[0].id);
              } else if (
                event.key === 'Backspace' &&
                !query &&
                selectedIds.length
              ) {
                removeParticipant(selectedIds[selectedIds.length - 1]);
              } else if (event.key === 'Escape') {
                setOpen(false);
              }
            }}
            aria-label='Participants'
            placeholder='Add people'
            className='min-w-32 flex-1 border-0 bg-transparent px-1 py-1 text-sm text-slate-900 outline-none placeholder:text-slate-400'
          />
        </div>
        {open && (
          <div
            id='private-thread-participant-options'
            role='listbox'
            className='absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-xl'
          >
            {suggestions.length ? (
              suggestions.map((user) => (
                <button
                  key={user.id}
                  type='button'
                  role='option'
                  aria-selected='false'
                  className='flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left hover:bg-slate-100'
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => addParticipant(user.id)}
                >
                  <Avatar
                    name={user.data.displayName}
                    size='sm'
                    showFallback
                    getInitials={() => getInitials(user.data.displayName)}
                    className={getAvatarColorClass(user.data.displayName)}
                  />
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate text-sm font-medium text-slate-900'>
                      {user.data.displayName}
                      {user.id === currentUserId ? ' (you)' : ''}
                    </span>
                    <span className='block truncate text-xs text-slate-500'>
                      {user.data.email || 'No email'}
                    </span>
                  </span>
                </button>
              ))
            ) : (
              <div className='px-3 py-4 text-center text-sm text-slate-500'>
                {query ? 'No matching people' : 'Everyone is already added'}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
